<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { string } from 'yup'
import { emailLoginAction } from '@/api/functions/auth/email-login.action'
import { useMutationApi } from '@/composables/useApi'
import { setAuth } from '@/composables/useAuth'
import { emailErr, requiredErr } from '@/constants'
import { toEnglishDigits } from '@/utils/to-english-number.util'

const router = useRouter()
const isRedirecting = ref(false)
const loading = ref(false)
const remember = ref(false)

const validationSchema = {
  email: string().required(requiredErr).email(emailErr),
  password: string()
    .transform((value: string) => toEnglishDigits(value))
    .required(requiredErr),
}

const { mutate: loginWithEmailMutate } = useMutationApi({
  mutationKey: ['loginWithEmail'],
  mutationFn: emailLoginAction,
  onSuccess: (result) => {
    setAuth(result.data.accessToken, result.data)
    router.push('/')
  },
  onError: (err: unknown) => {
    console.error(err)
  },
})

const loginFormSubmission = async (values: { email: unknown; password: unknown }) => {
  loading.value = true
  await loginWithEmailMutate({
    email: values.email as string,
    password: values.password as string,
    remember: true,
  })
  if (remember.value) {
    localStorage.setItem('rememberMe', JSON.stringify(values))
  } else {
    localStorage.removeItem('rememberMe')
  }
  loading.value = false
}

const initialValues = {
  email: '',
  password: '345345',
}
</script>

<template>
  <div class="flex items-center justify-center">
    <div v-if="isRedirecting" class="flex items-center justify-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>

    <div v-else class="flex flex-col items-center gap-6">
      <img src="/item-logo.d3553ee.svg" alt="payjib-logo" width="160" height="54" />
      <div class="w-[352px] bg-white rounded-2xl p-8">
        <!-- Login Form -->
        <UiForm
          @submit="loginFormSubmission"
          class="space-y-3"
          :validation-schema="validationSchema"
          :initial-values="initialValues"
        >
          <!-- Email Field -->
          <Input name="email" label="آدرس ایمیل" type="text" />
          <Input name="password" label="رمز عبور" type="password" />

          <!-- Remember Me & Forgot Password -->
          <div class="flex items-center justify-between mt-8">
            <label class="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" v-model="remember" class="hidden peer" />
              <div
                class="w-5 h-5 rounded-md border border-[#2d5396] peer-checked:bg-[#2d5396] peer-checked:border-[#2d5396] flex items-center justify-center duration-200"
              >
                <svg
                  v-if="remember"
                  class="w-3.5 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span class="ml-2 font-medium text-[13px] text-[#1b325a]">مرا به خاطر بسپار</span>
            </label>
          </div>

          <!-- Submit Button -->
          <Button
            :loading="loading"
            :isAdmin="true"
            type="submit"
            :disabled="loading"
            class="mt-9 w-full rounded-lg font-medium text-[#0f1114] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span class="">ورود</span>
          </Button>
        </UiForm>
      </div>
    </div>
  </div>
</template>
